import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/hooks/useAuth';
import { getPrivateCacheRebuildStatus } from '../services/dashboardApi';

export interface UsePrivateCacheRebuildStatusResult {
  isRebuilding: boolean;
  startedAt: string | null;
  opId: string | null;
  cacheBuiltAt: string | null;
}

function getCachedSelfDashboardData(queryClient: ReturnType<typeof useQueryClient>, userId?: string): any {
  if (!userId) return null;
  const matches = queryClient.getQueriesData({ queryKey: ['dashboard', 'self', userId] });
  const match = matches.find(([, data]) => Boolean(data));
  return match?.[1] ?? null;
}

/**
 * Hook to poll private cache rebuild status for toast synchronization.
 * Only enabled when user is viewing self portfolio (not friend portfolio).
 * 
 * Polls when:
 * - private_rebuilding === true (from status endpoint), OR
 * - dashboard.meta.refresh_needed === true (from dashboard response - checked via query cache)
 * 
 * The cheap status endpoint triggers dashboard refreshes when private cache_built_at advances.
 */
export function usePrivateCacheRebuildStatus(): UsePrivateCacheRebuildStatusResult {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const wasRebuildingRef = useRef(false);
  const currentOpIdRef = useRef<string | null>(null);
  const lastStatusCacheBuiltAtRef = useRef<string | null>(null);

  // Get dashboard data from query cache (don't fetch, just read cached data)
  const dashboardData = getCachedSelfDashboardData(queryClient, user?.id);

  const { data: statusData } = useQuery({
    queryKey: ['private-cache-rebuild-status', user?.id],
    queryFn: async () => {
      if (!session?.access_token) throw new Error('No auth token');
      return getPrivateCacheRebuildStatus(session.access_token);
    },
    enabled: !!session?.access_token && !!user?.id,
    refetchInterval: (query) => {
      // Determine if we should poll
      const data = query.state.data;
      const isRebuilding = data?.private_rebuilding ?? false;
      
      // Poll if:
      // 1. Backend says it's rebuilding, OR
      // 2. Dashboard has refresh_needed=true (even without op_id initially - it will be set soon)
      //    This ensures we start polling immediately when a rebuild is queued
      const shouldPoll = 
        isRebuilding || 
        (dashboardData?.meta?.refresh_needed === true);
      
      if (shouldPoll) {
        return 1500; // 1.5 seconds
      }
      return false; // Stop polling when not rebuilding and refresh_needed is false
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });

  // Track op_id from dashboard response (when it changes)
  useEffect(() => {
    if (dashboardData?.meta?.refresh_op_id) {
      const newOpId = dashboardData.meta.refresh_op_id;
      
      // Only update if op_id changed
      if (newOpId !== currentOpIdRef.current) {
        currentOpIdRef.current = newOpId;
      }
    }
  }, [dashboardData?.meta?.refresh_op_id]);

  // Track when rebuild completes to trigger dashboard refresh
  useEffect(() => {
    const isRebuilding = statusData?.private_rebuilding ?? false;
    const wasRebuilding = wasRebuildingRef.current;
    
    // If rebuild just completed (was true, now false), invalidate dashboard query once
    if (wasRebuilding && !isRebuilding && user?.id) {
      // Invalidate all dashboard queries (matches ['dashboard', 'self', user.id, tier, window])
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
      // Also refetch immediately to ensure fresh data
      queryClient.refetchQueries({ queryKey: ['dashboard', 'self', user.id] });
      // Clear op_id tracking since rebuild is done
      currentOpIdRef.current = null;
    }
    
    wasRebuildingRef.current = isRebuilding;
  }, [statusData?.private_rebuilding, user?.id, queryClient]);

  // Detect when private caches are ready from the cheap status endpoint.
  useEffect(() => {
    if (!user?.id) return;

    const statusCacheBuiltAt = statusData?.cache_built_at || null;
    if (
      statusCacheBuiltAt
      && statusCacheBuiltAt !== lastStatusCacheBuiltAtRef.current
    ) {
      lastStatusCacheBuiltAtRef.current = statusCacheBuiltAt;
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
      queryClient.refetchQueries({ queryKey: ['dashboard', 'self', user.id] });
      currentOpIdRef.current = null;
    }
  }, [statusData?.cache_built_at, user?.id, queryClient]);

  return {
    isRebuilding: statusData?.private_rebuilding ?? false,
    startedAt: statusData?.started_at ?? null,
    opId: statusData?.op_id ?? null,
    cacheBuiltAt: statusData?.cache_built_at ?? null,
  };
}
